<template>
  <div>
    <v-breadcrumbs :items="routes">
      <template #divider>
        <app-icon
          icon-name="angle-right"
          icon-weight="fas"
          style="opacity: .4;"
        />
      </template>
    </v-breadcrumbs>

    <h1>{{$t('project.views.SigningChecklistOrder.title')}}</h1>

    <div class="d-flex justify-space-between my-4">
      <app-button
        :to="{ name: SIGNING_CHECKLIST_ROUTE_NAME }"
        type="outlined"
        variant="neutral"
      >
        <app-icon
          icon-name="long-arrow-alt-left"
          icon-weight="fas"
          class="mr-2"
        />
        {{$t('project.views.SigningChecklistOrder.backBtnLabel')}}
      </app-button>

      <app-button
        v-if="canAddSignatureGroup"
        color="primary"
        :disabled="signatureGroupsEditionPending"
        @click="addGroup(stepGroups.length)"
      >
        {{ $t('project.views.SigningChecklistOrder.addGroupBtnLabel') }}
      </app-button>
    </div>

    <template v-if="areSignaturesGroupsLoading">
      <v-row>
        <v-col v-if="$vuetify.display.mdAndUp" md="4">
          <v-skeleton-loader type="list-item@3" />
        </v-col>
        <v-col md="8">
          <v-skeleton-loader type="heading, list-item@3" />
        </v-col>
      </v-row>
    </template>
    <template v-else-if="hasAtLeastOneSigningChecklist">
      <app-alert-in-page class="mb-4">
        <p>{{ $t('project.views.SigningChecklistOrder.topHint.line1') }}</p>
        <p>{{ $t('project.views.SigningChecklistOrder.topHint.line2') }}</p>
      </app-alert-in-page>

      <v-row>
        <v-col :md="4">
          <div class="card pa-4 h-100 position-relative">
            <draggable
              v-model="stepsInNoGroup"
              item-key="id"
              :group="{
                name: 'available-steps',
                pull: ['steps-group'],
                put: ['steps-group'],
              }"
              :empty-insert-threshold="12"
              :component-data="{
                class: 'not-in-group-draggable-area',
              }"
            >
              <template #item="{ element: step }">
                <SigningChecklistStepToOrder :title="step.title" :index="step.position || step.display" />
              </template>
            </draggable>

            <span v-if="stepsInNoGroup.length === 0"
                  class="text--small empty-list-placeholder"
            >
              {{ $t('project.views.SigningChecklistOrder.stepsWithoutGroupPlaceholder') }}
          </span>
          </div>
        </v-col>

        <v-col :md="8" class="overflow-y-auto">
          <div class="card">
            <draggable
              v-model="stepGroups"
              item-key="id"
              @change="moveGroup"
            >
              <template #item="{ element: group, index }">
                <SigningChecklistStepsGroup
                  :order="index + 1"
                  :steps="group.steps"
                  @add-group="addGroup(index + 1)"
                  @delete-group="showDeleteGroupModal(group.id)"
                  @delete-step="deleteStep"
                  @move-step="step => moveStep(step, group)"
                />
              </template>
            </draggable>
          </div>
        </v-col>
      </v-row>
    </template>
    <template v-else>
      <p class="mt-16 d-flex align-center justify-center">
        {{ $t('project.views.SigningChecklistOrder.noData') }}
      </p>
    </template>

    <SigningChecklistOrderDeleteGroupDialog
      v-if="deleteGroupModal"
      @confirm="confirmGroupDelete"
      @close="deleteGroupModal = false"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import SigningChecklistStepsGroup from '@/project/signing-checklist/order-signature/SigningChecklistStepsGroup.vue'
import SigningChecklistStepToOrder from '@/project/signing-checklist/order-signature/SigningChecklistStepToOrder.vue'
import SigningChecklistOrderDeleteGroupDialog from '@/project/signing-checklist/SigningChecklistOrderDeleteGroupDialog.vue'
import { ROOM_DOCUMENTS_ROUTE_NAME, SIGNING_CHECKLIST_ROUTE_NAME, ROOM_ROUTE_TYPES } from '@/router'
import { GET_SIGNING_CHECKLIST } from '@/store/modules/signing-checklist/action_types'
import { SET_SIGNING_CHECKLIST } from '@/store/modules/signing-checklist/mutation_types'

export default {
  name: 'SigningChecklistOrder',
  components: {
    SigningChecklistOrderDeleteGroupDialog,
    SigningChecklistStepToOrder,
    SigningChecklistStepsGroup,
    draggable,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      SIGNING_CHECKLIST_ROUTE_NAME,
      routes: [
        {
          exact: true,
          to: {
            name: SIGNING_CHECKLIST_ROUTE_NAME,
          },
          title: this.$t('project.views.SigningChecklistOrder.initialBreadcrumb'),
        },
        {
          title: this.$t('project.views.SigningChecklistOrder.title'),
        },
      ],
      deleteGroupModal: false,
      groupPendingDelete: 0,
      stepGroups: [],
      stepsInNoGroup: [],
    }
  },
  head () {
    return {
      title: this.$t('project.views.SigningChecklistOrder.metaTitle'),
    }
  },
  computed: {
    ...mapState('signingChecklist', ['signingChecklistPending']),
    ...mapState('signingChecklistOrder', [
      'signatureGroupsLoadingPending',
      'signatureGroups',
    ]),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('signingChecklist', ['flatSigningChecklist']),
    ...mapGetters('signingChecklistOrder', ['signatureGroupsEditionPending']),
    hasAtLeastOneSigningChecklist () {
      return this.flatSigningChecklist.length !== 0
    },
    areSignaturesGroupsLoading () {
      return this.signingChecklistPending || this.signatureGroupsLoadingPending
    },
    canAddSignatureGroup () {
      return !this.areSignaturesGroupsLoading && this.hasAtLeastOneSigningChecklist
    },
  },
  watch: {
    stepGroups (newValue) {
      const stepIdsAlreadyInGroups = this.getStepIdsInGroupList(newValue)
      this.stepsInNoGroup = this.flatSigningChecklist.filter((step) => !stepIdsAlreadyInGroups.includes(step.id) && (step.envelope && step.envelope.status === 'draft'))
    },
  },
  async created () {
    if (this.currentRoom.isDataroom) {
      this.$router.replace({ name: ROOM_DOCUMENTS_ROUTE_NAME, params: { roomType: ROOM_ROUTE_TYPES.DATAROOM } })
    }
    await this.GET_SIGNING_CHECKLIST({ mnemo: this.mnemo })
    if (this.hasAtLeastOneSigningChecklist) {
      await this.GET_SIGNING_CHECKLIST_GROUPS(this.mnemo)
      this.stepGroups = this.signatureGroups
    }
  },
  unmounted () {
    // Make it empty again
    this.SET_SIGNING_CHECKLIST([])
  },
  methods: {
    ...mapMutations('signingChecklist', [SET_SIGNING_CHECKLIST]),
    ...mapActions('signingChecklist', [GET_SIGNING_CHECKLIST]),
    ...mapActions('signingChecklistOrder', [
      'CREATE_SIGNING_CHECKLIST_GROUP',
      'DELETE_SIGNING_CHECKLIST_GROUP',
      'DELETE_SIGNING_CHECKLIST_STEP',
      'GET_SIGNING_CHECKLIST_GROUPS',
      'MOVE_SIGNING_CHECKLIST_GROUP',
      'MOVE_SIGNING_CHECKLIST_STEP',
    ]),
    getStepIdsInGroupList (stepGroups) {
      const ids = []
      stepGroups.forEach((group) => {
        group.steps.forEach((step) => {
          ids.push(step.id)
        })
      })
      return ids
    },
    async addGroup (position) {
      try {
        const createdGroup = await this.CREATE_SIGNING_CHECKLIST_GROUP({
          mnemo: this.mnemo,
          position,
        })
        this.stepGroups.splice(position, 0, { id: createdGroup.id, position, steps: [] })
      } catch (e) {
        console.error(e)
      }
    },
    async deleteGroup (groupId) {
      await this.DELETE_SIGNING_CHECKLIST_GROUP({
        mnemo: this.mnemo,
        groupId,
      })
      this.stepGroups = this.stepGroups.filter((group) => group.id !== groupId)
    },
    async moveGroup ($event) {
      if (!$event.moved) {
        return
      }

      const { element, newIndex } = $event.moved
      this.MOVE_SIGNING_CHECKLIST_GROUP({
        mnemo: this.mnemo,
        blockId: element.id,
        position: newIndex,
      })
    },
    async moveStep (step, newStepGroup) {
      await this.MOVE_SIGNING_CHECKLIST_STEP({
        mnemo: this.mnemo,
        stepId: step.id,
        groupId: newStepGroup.id,
      })
    },
    async deleteStep (step) {
      await this.DELETE_SIGNING_CHECKLIST_STEP({
        mnemo: this.mnemo,
        stepId: step.id,
      })

      this.stepsInNoGroup.push(step)

      for (const [index, group] of this.stepGroups.entries()) {
        if (group.steps.find(s => s.id === step.id)) {
          this.stepGroups[index].steps = this.stepGroups[index].steps.filter(s => s.id !== step.id)
          break
        }
      }
    },
    showDeleteGroupModal (groupIdToDelete) {
      this.groupPendingDelete = groupIdToDelete
      this.deleteGroupModal = true
    },
    confirmGroupDelete () {
      this.deleteGroup(this.groupPendingDelete)
      this.groupPendingDelete = 0
    },
  },
}
</script>

<style scoped lang="scss">
.card {
  background-color: #FFFFFF;
  border-radius: 8px;
}

.not-in-group-draggable-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}

.empty-list-placeholder {
  position: absolute;
  top: 24px;
  left: 24px;
  right:24px;
  text-align: center;

  .not-in-group-draggable-area:not(:empty) + & {
    display: none;
  }
}
</style>
